import {
  useRef,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  MouseEventHandler
} from "react";
import { Tooltip } from "bootstrap";
import TooltipComponent from "./TooltipComponent";
import CompanyService, { DataType } from "../../services/CompanyService";
import { SortDirection } from "./entities/Sort";
import TableContext from "../task/TableContext";
import React from "react";
import { TranslationService } from "../../services/TranslationService";
import { Switch } from "../../utils/Utils";
import Empty from "./components/Empty";
import ErrorMessage from "./components/Error";
import TableLoader from "./components/TableLoader";
import Paginator from "./Paginator";
import {
  TableHeader,
  TableHeaderWithFieldId
} from "../client/components/TableHeader";
import {
  TableId,
  usesWidthContract,
  WIDTH_CONTRACT_CLASS
} from "./tableWidth/tableWidthRegistry";
import "./tableWidth/tableWidth.css";

export { TableHeaderWithFieldId, TableHeader };
export type TableProps<TResponse extends { list: unknown[] }> = {
  item: (props: TResponse) => JSX.Element;
  headers: TableHeader[];
  title?: string;
  children?: ReactNode;
  stickyHeader?: boolean;
  emptyMessage?: string;
  limitSize?: boolean;
  getKey?: (item: TResponse["list"][number], index: number) => string | number;
  additionalClass?: any;
  /**
   * Cuando es true, en vez del tope fijo por defecto (mh-55p / additionalClass),
   * el cuerpo de la tabla ocupa dinámicamente el alto disponible hasta el final
   * del viewport (recalculado en resize y cuando cambia el contenido de arriba).
   */
  fillHeight?: boolean;
  /**
   * Identificador de la tabla. Si está en TABLES_WITH_WIDTH_CONTRACT, la tabla
   * usa el contrato de anchos (docs/ANCHO_COLUMNAS_TABLAS.md). Si no se pasa, o
   * si no está registrada, el comportamiento es exactamente el anterior.
   */
  tableId?: TableId;
};

export const Table = ({
  item,
  headers,
  getKey,
  limitSize = false,
  title = undefined,
  children,
  stickyHeader = false,
  emptyMessage,
  additionalClass,
  fillHeight = false,
  tableId
}: TableProps<any>) => {
  const tableTheadRef = useRef<null | HTMLDivElement>(null);
  const tableTbodyRef = useRef<null | HTMLDivElement>(null);
  const tableTfootRef = useRef<null | HTMLDivElement>(null);
  const paginatorRef = useRef<null | HTMLDivElement>(null);
  const widthCalculationRef = useRef<{
    lastResponseLength?: number;
    isCalculating: boolean;
    animationFrameId?: number;
  }>({ isCalculating: false });
  const recalculationTimeoutRef = useRef<number | undefined>(undefined);
  const columnWidthResizeTimeoutRef = useRef<number | undefined>(undefined);
  const fillHeightRafRef = useRef<number | undefined>(undefined);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] =
    React.useState(false);

  // Función para calcular el ancho de la barra de scroll
  const getScrollbarWidth = React.useCallback(
    (element: HTMLElement): number => {
      // Primero intentar obtener el ancho real del scrollbar del elemento si ya tiene scroll
      if (element.scrollHeight > element.clientHeight) {
        const scrollbarWidth = element.offsetWidth - element.clientWidth;
        if (scrollbarWidth > 0) {
          return scrollbarWidth;
        }
      }

      // Si no hay scroll activo, crear un elemento temporal para medir el scrollbar
      const outer = document.createElement("div");
      outer.style.visibility = "hidden";
      outer.style.overflow = "scroll";
      outer.style.width = "100px";
      outer.style.height = "100px";
      outer.style.position = "absolute";
      outer.style.top = "-9999px";
      // Necesario para IE - usar setProperty para evitar error de TypeScript
      (outer.style as any).msOverflowStyle = "scrollbar";
      document.body.appendChild(outer);

      const inner = document.createElement("div");
      inner.style.width = "100%";
      inner.style.height = "200px"; // Más alto que el contenedor para forzar scroll
      outer.appendChild(inner);

      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

      outer.parentNode?.removeChild(outer);

      return scrollbarWidth;
    },
    []
  );

  // Función para verificar si se necesita scroll horizontal
  const checkHorizontalScrollNeeded = React.useCallback(() => {
    const container = tableTbodyRef.current;
    const table = container?.querySelector("table");

    if (!container || !table) {
      setNeedsHorizontalScroll(false);
      return;
    }

    // Obtener el ancho disponible del contenedor (clientWidth ya excluye el scrollbar si está presente)
    const containerWidth = container.clientWidth;
    const tableWidth = table.scrollWidth;

    // Agregar un pequeño margen de tolerancia (2px) para evitar falsos positivos
    // debido a redondeos o diferencias mínimas en el cálculo
    const tolerance = 2;

    // Solo mostrar scroll horizontal si el ancho de la tabla es significativamente mayor
    // al ancho disponible del contenedor (considerando el margen de tolerancia)
    // El clientWidth ya considera el scrollbar vertical si está presente
    const needsScroll = tableWidth > containerWidth + tolerance;

    setNeedsHorizontalScroll(needsScroll);
  }, []);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    const tableContainerThead = tableTheadRef.current;
    const tableContainerTbody = tableTbodyRef.current;
    const tableContainerTfoot = tableTfootRef.current;
    if (tableContainerThead === null || tableContainerTbody === null) {
      return;
    }
    tableContainerThead.scrollLeft = tableContainerTbody.scrollLeft;
    if (tableContainerTfoot) {
      tableContainerTfoot.scrollLeft = tableContainerTbody.scrollLeft;
    }
  };

  const { error, response, loading, currentPage, reload } =
    useContext(TableContext);

  // Clase que habilita el contrato de anchos. `lang-*` resuelve el token de la
  // columna de fecha, que depende del idioma porque formatDateShort usa mes
  // textual localizado (defecto D12 de la documentación).
  const widthContract = usesWidthContract(tableId);
  const widthContractClass = widthContract
    ? ` ${WIDTH_CONTRACT_CLASS} lang-${TranslationService.currentLanguage}`
    : "";
  const Item = item;
  const extractKey =
    getKey ?? ((x, index) => `tableItem_${currentPage}_${index}`);

  // Función reutilizable para calcular anchos de columnas
  const recalculateColumnWidths = React.useCallback(() => {
    if (
      !response ||
      !response.list ||
      response.list.length === 0 ||
      loading ||
      limitSize
    ) {
      return;
    }

    const currentRef = widthCalculationRef.current;

    // Prevenir ejecuciones múltiples
    if (currentRef.isCalculating) {
      return;
    }

    // Cancelar frame anterior si existe
    if (currentRef.animationFrameId) {
      cancelAnimationFrame(currentRef.animationFrameId);
    }

    currentRef.isCalculating = true;

    const animationFrameId = requestAnimationFrame(() => {
      currentRef.animationFrameId = animationFrameId;
      const container = tableTbodyRef.current;
      const thList = Array.from(
        tableTheadRef.current?.querySelectorAll("th") ?? []
      ) as HTMLTableCellElement[];
      const th = thList[0];

      if (th === undefined || !container) {
        currentRef.isCalculating = false;
        return;
      }

      // Detectar si habrá scroll vertical
      // El clientWidth ya excluye el scrollbar si está presente, pero necesitamos
      // considerar si el scrollbar aparecerá para ajustar el espacio disponible
      const willHaveVerticalScroll =
        container.scrollHeight > container.clientHeight;

      // Calcular el ancho del scrollbar que se usará
      // Si ya hay scroll activo, el clientWidth ya lo tiene en cuenta
      // Pero si aún no aparece, necesitamos calcularlo para ajustar el espacio
      let scrollbarWidth = 0;
      if (willHaveVerticalScroll) {
        // Intentar obtener el ancho real del scrollbar del elemento
        const realScrollbarWidth =
          container.offsetWidth - container.clientWidth;
        if (realScrollbarWidth > 0 && realScrollbarWidth < 50) {
          // Si el scrollbar ya está presente, usar su ancho real
          scrollbarWidth = realScrollbarWidth;
        } else {
          // Si aún no se ha renderizado, calcular el ancho potencial
          // Usar un valor conservador (típicamente 15-17px en la mayoría de navegadores)
          scrollbarWidth = getScrollbarWidth(container);
        }
      }

      // El ancho disponible es el clientWidth (que ya excluye scrollbar si está presente)
      // menos el scrollbar que aparecerá si aún no está presente
      // Si el scrollbar ya está presente, clientWidth ya lo tiene en cuenta
      const hasScrollbarNow = container.offsetWidth > container.clientWidth;
      const availableContainerWidth = hasScrollbarNow
        ? container.clientWidth
        : container.clientWidth - scrollbarWidth;

      // Detectar campos adicionales con Width definido para no sobrescribirlos
      const additionalFieldsWithFixedWidth = new Set<number>();
      headers.forEach((tableHeader, index) => {
        const isAdditional =
          tableHeader.key.includes("additional") && "fieldId" in tableHeader;
        if (isAdditional) {
          const definition = CompanyService.getAdditionalDefinitions().find(
            (x) =>
              x.AdditionalDefinitionID.toString() ===
              (tableHeader as TableHeaderWithFieldId).fieldId
          );
          // En el contrato el criterio tiene que ser el mismo que usa
          // InvoiceService para no emitir clase de ancho (§4.5) y el que usa
          // `Headers` para emitir th-w-160 / th-w-240: sólo esos dos valores
          // dejan a la columna con un ancho propio. Con cualquier otro Width la
          // columna sí tiene que entrar en el reparto, o queda sin ancho.
          const isFixed = widthContract
            ? definition?.Width === 160 || definition?.Width === 240
            : !!definition?.Width;
          if (isFixed) {
            additionalFieldsWithFixedWidth.add(index);
          }
        }
      });

      // Limpiar estilos anteriores de th y td, pero NO los que tienen ancho fijo.
      // En el contrato se limpia también `width`: si queda el inline del cálculo
      // anterior, la medición devuelve ese valor y la columna puede comprimirse
      // pero nunca volver a expandirse cuando la ventana crece.
      thList.forEach((th, index) => {
        if (!additionalFieldsWithFixedWidth.has(index)) {
          th.style.minWidth = "";
          th.style.maxWidth = "";
          if (widthContract) {
            th.style.width = "";
          }
        }
      });

      const tbodyTdList = Array.from(
        tableTbodyRef.current!.querySelectorAll("tbody td")
      ) as HTMLTableCellElement[];
      const tfootTdList = Array.from(
        tableTfootRef.current?.querySelectorAll("td") ?? []
      ) as HTMLTableCellElement[];
      const tdList = [...tbodyTdList, ...tfootTdList];

      const indexTD = tdList.map((td) => {
        const parentTD = td.parentElement;
        const children = Array.from(parentTD!.children);
        return children.indexOf(td);
      });

      // Limpiar estilos de td, pero NO los que tienen ancho fijo
      tdList.forEach((td, index) => {
        const thIndex = indexTD[index];
        if (
          thIndex !== undefined &&
          !additionalFieldsWithFixedWidth.has(thIndex)
        ) {
          td.style.minWidth = "";
          td.style.maxWidth = "";
          if (widthContract) {
            td.style.width = "";
          }
        }
      });

      // Forzar reflow para asegurar que los estilos CSS estén aplicados antes de calcular
      thList.forEach((th) => th.offsetHeight);

      // Defecto D3: los límites del CSS se leen UNA sola vez, acá, con los estilos
      // inline ya limpiados y antes de que el bucle de escritura ponga los suyos.
      // Leerlos dentro de ese bucle (como sigue haciendo el camino legacy) devuelve
      // el valor que el propio bucle acaba de escribir, no el del CSS.
      const parseCssLimit = (value: string): number | undefined => {
        if (!value || value === "none" || value === "auto" || value === "0px") {
          return undefined;
        }
        const match = value.match(/(\d+\.?\d*)px/);
        return match ? parseFloat(match[1]) : undefined;
      };
      const columnCssLimits = widthContract
        ? thList.map((th) => {
            const computed = window.getComputedStyle(th);
            return {
              min: parseCssLimit(computed.minWidth),
              max: parseCssLimit(computed.maxWidth)
            };
          })
        : [];

      // Columnas rígidas (R3): no participan del reparto ni de la compresión.
      // En legacy el conjunto es exactamente el de campos adicionales con Width
      // configurado, que es el comportamiento anterior.
      const rigidColumns = new Set<number>(additionalFieldsWithFixedWidth);
      if (widthContract) {
        headers.forEach((tableHeader, index) => {
          const type = (tableHeader as TableHeaderWithFieldId).type;
          if (
            type === DataType.Date ||
            type === DataType.Currency ||
            type === DataType.Number
          ) {
            rigidColumns.add(index);
          }
          // Columnas de ancho exacto (checkbox, iconos, fechas): min === max.
          const limits = columnCssLimits[index];
          if (limits?.min !== undefined && limits.min === limits.max) {
            rigidColumns.add(index);
          }
        });
      }

      // Ancho que el DATO necesita para no recortarse, por columna.
      //
      // El label del header sí puede recortarse (para eso tiene tooltip), pero el
      // dato no: un monto con ellipsis es un número equivocado. Por eso se mide
      // sobre las celdas del cuerpo y de la fila de totales, nunca sobre el th.
      //
      // Se mide con un Range sobre el contenido en vez de `td.offsetWidth`:
      // offsetWidth devuelve el ancho de la celda YA estirada por el auto-layout
      // (medido: 762px en una celda cuyo texto ocupa 119.28px), mientras que el
      // Range devuelve siempre el ancho del contenido. Sólo se aplica a columnas
      // de texto inline — fecha, monto, número —; las de checkbox e icono tienen
      // hijos de bloque, cuyo ancho vuelve a ser el de la celda estirada.
      const contentNeeded: number[] = [];
      if (widthContract) {
        const measureRange = document.createRange();
        const measurableTypes = [
          DataType.Date,
          DataType.Currency,
          DataType.Number
        ];
        const measurableColumns = new Set<number>();
        headers.forEach((tableHeader, index) => {
          const type = (tableHeader as TableHeaderWithFieldId).type;
          if (measurableTypes.includes(type)) {
            measurableColumns.add(index);
          }
        });

        tdList.forEach((td, index) => {
          const thIndex = indexTD[index];
          if (thIndex === undefined || !measurableColumns.has(thIndex)) {
            return;
          }
          measureRange.selectNodeContents(td);
          const contentWidth = measureRange.getBoundingClientRect().width;
          if (contentWidth === 0) {
            return;
          }
          const style = window.getComputedStyle(td);
          // +1 porque más adelante el ancho viaja por `offsetWidth`, que es
          // entero: sin ese píxel el redondeo hacia abajo recorta el dato.
          const needed =
            Math.ceil(
              contentWidth +
                parseFloat(style.paddingLeft) +
                parseFloat(style.paddingRight)
            ) + 1;
          if (needed > (contentNeeded[thIndex] ?? 0)) {
            contentNeeded[thIndex] = needed;
          }
        });
        measureRange.detach();
      }

      // Calcular anchos
      const itemWidth = thList.map((th) => th.offsetWidth);

      const firstRow = tableTbodyRef.current?.children
        .item(0)
        ?.children.item(0);

      // Validar que el número de columnas coincida entre header y body
      const headerColumnCount = thList.length;
      const bodyColumnCount = firstRow
        ? Array.from(firstRow.children).length
        : 0;
      if (headerColumnCount !== bodyColumnCount && bodyColumnCount > 0) {
        console.warn(
          `Column count mismatch: header has ${headerColumnCount}, body has ${bodyColumnCount}`
        );
      }
      const firstRowChildren = firstRow ? Array.from(firstRow.children) : [];
      const rowHasColspan = !firstRowChildren.every((x) => {
        const rowChildren = Array.from((x as HTMLTableRowElement).children);
        return rowChildren.every(
          (y) => (y as HTMLTableCellElement).colSpan === 1
        );
      });

      if (!rowHasColspan) {
        headers.forEach((tableHeader, indexTableHeader) => {
          if (tableHeader.numeric !== true) {
            return;
          }

          const tdsFromHeader = tdList.filter((td, index) => {
            const thIndex = indexTD[index];
            return thIndex === indexTableHeader;
          });

          tdsFromHeader.forEach((td) => {
            const numericTdWidth = td.offsetWidth;
            if (numericTdWidth > itemWidth[indexTableHeader]) {
              itemWidth[indexTableHeader] = numericTdWidth;
            }
          });
        });
      }

      // Punto de partida del reparto (§4.4).
      if (widthContract) {
        itemWidth.forEach((width, index) => {
          const limits = columnCssLimits[index];
          let value = width;
          if (rigidColumns.has(index)) {
            // Rígida: se ajusta al dato, acotada a su rango. Para las de ancho
            // exacto (min === max) el resultado es siempre el mismo valor.
            if (limits?.max !== undefined && value > limits.max) {
              value = limits.max;
            }
            if (limits?.min !== undefined && value < limits.min) {
              value = limits.min;
            }
            // El techo del contrato es una preferencia, no un permiso para
            // recortar el dato: si el contenido no entra, la columna crece.
            const needed = contentNeeded[index];
            if (needed !== undefined && needed > value) {
              value = needed;
            }
          } else if (limits?.max !== undefined) {
            // Flexible: parte de su ancho preferido (el máximo del contrato) y
            // se comprime hacia su mínimo según el espacio disponible. Al no
            // depender del contenido de la página, los anchos son estables
            // entre páginas, filtros y recargas (R6).
            value = limits.max;
          }
          itemWidth[index] = value;
        });
      }

      // Calcular el ancho total de todas las columnas
      let totalColumnsWidth = 0;
      let fixedColumnsWidth = 0;
      const flexibleColumns: number[] = [];

      itemWidth.forEach((width, index) => {
        totalColumnsWidth += width;
        if (rigidColumns.has(index)) {
          fixedColumnsWidth += width;
        } else {
          flexibleColumns.push(index);
        }
      });

      // Si el ancho total excede el espacio disponible (considerando scrollbar vertical),
      // ajustar proporcionalmente solo las columnas flexibles para que quepan
      let widthAdjustmentFactor = 1;
      if (
        totalColumnsWidth > availableContainerWidth &&
        availableContainerWidth > 0 &&
        flexibleColumns.length > 0
      ) {
        // Calcular el espacio disponible para columnas flexibles (restando los anchos fijos)
        const availableForFlexible = Math.max(
          0,
          availableContainerWidth - fixedColumnsWidth
        );
        const flexibleColumnsWidth = totalColumnsWidth - fixedColumnsWidth;

        if (flexibleColumnsWidth > 0) {
          widthAdjustmentFactor = availableForFlexible / flexibleColumnsWidth;
          if (!widthContract) {
            // Asegurar que el factor no sea menor a 0.5 para evitar columnas demasiado pequeñas
            widthAdjustmentFactor = Math.max(0.5, widthAdjustmentFactor);
          }
          // En el contrato no hace falta el piso de 0.5: cada columna flexible se
          // frena en su propio min y, si aun así no entra, aparece scroll
          // horizontal (R4) en vez de comprimir por debajo del mínimo.
        }
      }

      // Aplicar nuevos anchos, pero respetar anchos fijos de campos adicionales
      indexTD.forEach((indexTH, index) => {
        const td = tdList[index];
        const th = thList[indexTH];

        // Si tiene ancho fijo, aplicar el ancho del header directamente
        if (additionalFieldsWithFixedWidth.has(indexTH)) {
          const fixedWidth = th.offsetWidth;
          if (fixedWidth) {
            td.style.minWidth = fixedWidth + "px";
            td.style.maxWidth = fixedWidth + "px";
          }
          return;
        }

        // Límites de la columna. En el contrato salen del cache leído antes de
        // escribir nada (defecto D3); en legacy se conserva la lectura por
        // iteración, que se contamina con lo que este mismo bucle ya escribió.
        let hasCssMinWidth: boolean;
        let hasCssMaxWidth: boolean;
        let cssMinWidthValue = 0;
        let cssMaxWidthValue = Infinity;

        if (widthContract) {
          const limits = columnCssLimits[indexTH];
          hasCssMinWidth = limits?.min !== undefined;
          hasCssMaxWidth = limits?.max !== undefined;
          cssMinWidthValue = limits?.min ?? 0;
          cssMaxWidthValue = limits?.max ?? Infinity;

          // El techo cede ante el dato: si el contenido de la columna no entra
          // en el máximo del contrato, el máximo efectivo es el contenido. Sin
          // esto los dos clamps de más abajo vuelven a recortar el monto.
          const needed = contentNeeded[indexTH];
          if (needed !== undefined && needed > cssMaxWidthValue) {
            cssMaxWidthValue = needed;
          }
        } else {
          const computedStyle = window.getComputedStyle(th);
          const cssMinWidth = computedStyle.minWidth;
          const cssMaxWidth = computedStyle.maxWidth;
          hasCssMinWidth = !!(
            cssMinWidth &&
            cssMinWidth !== "0px" &&
            cssMinWidth !== "none" &&
            cssMinWidth !== "auto"
          );
          hasCssMaxWidth = !!(
            cssMaxWidth &&
            cssMaxWidth !== "none" &&
            cssMaxWidth !== "0px" &&
            cssMaxWidth !== "auto"
          );

          if (hasCssMinWidth) {
            const match = cssMinWidth.match(/(\d+\.?\d*)px/);
            if (match) {
              cssMinWidthValue = parseFloat(match[1]);
            }
          }

          if (hasCssMaxWidth) {
            const match = cssMaxWidth.match(/(\d+\.?\d*)px/);
            if (match) {
              cssMaxWidthValue = parseFloat(match[1]);
            }
          }
        }

        // Aplicar el ancho calculado, ajustado si es necesario para considerar el scrollbar vertical.
        // Las columnas rígidas (R3) quedan fuera de la compresión: comprimir una
        // fecha o un monto destruye el dato en vez de recortarlo (defecto D4).
        let thWidth = itemWidth[indexTH];
        if (widthAdjustmentFactor < 1 && !rigidColumns.has(indexTH)) {
          // Si tiene un mínimo CSS, respetarlo; si no, usar un mínimo razonable (120px en lugar de 50px)
          const minWidth = hasCssMinWidth ? cssMinWidthValue : 120;
          thWidth = Math.max(
            minWidth,
            Math.floor(thWidth * widthAdjustmentFactor)
          );
        }

        // Respetar el max-width CSS si existe
        if (hasCssMaxWidth && thWidth > cssMaxWidthValue) {
          thWidth = cssMaxWidthValue;
        }

        // Pequeño ajuste para evitar overflow, mínimo 1px. No se aplica a las
        // columnas rígidas del contrato: su ancho sale del dato o de un token
        // exacto, así que restarle un píxel es exactamente lo que hace que el
        // monto termine con ellipsis.
        if (!widthContract || !rigidColumns.has(indexTH)) {
          thWidth = Math.max(1, thWidth - 1);
        }

        // Aplicar siempre el mismo ancho a th y td para mantener sincronización
        if (thWidth > 0) {
          // Si hay CSS min-width y nuestro ancho es menor, usar el CSS min
          const finalWidth =
            hasCssMinWidth && thWidth < cssMinWidthValue
              ? cssMinWidthValue
              : thWidth;

          // Si hay CSS max-width y nuestro ancho es mayor, usar el CSS max
          const finalWidthClamped =
            hasCssMaxWidth && finalWidth > cssMaxWidthValue
              ? cssMaxWidthValue
              : finalWidth;

          // Aplicar siempre a ambos elementos para mantener sincronización
          td.style.width = finalWidthClamped.toString() + "px";
          td.style.minWidth = finalWidthClamped.toString() + "px";
          td.style.maxWidth = finalWidthClamped.toString() + "px";

          thList[indexTH].style.width = finalWidthClamped.toString() + "px";
          thList[indexTH].style.minWidth = finalWidthClamped.toString() + "px";
          thList[indexTH].style.maxWidth = finalWidthClamped.toString() + "px";
        }
      });

      currentRef.isCalculating = false;
      currentRef.lastResponseLength = response.list.length;

      // Verificar si se necesita scroll horizontal después de calcular anchos
      // Usar un delay más largo para asegurar que el DOM y el scrollbar se hayan actualizado completamente
      setTimeout(() => {
        checkHorizontalScrollNeeded();
      }, 100);
    });

    currentRef.animationFrameId = animationFrameId;
  }, [
    response,
    loading,
    limitSize,
    headers,
    widthContract,
    checkHorizontalScrollNeeded,
    getScrollbarWidth
  ]);

  // Efecto principal para calcular anchos cuando cambian las dependencias principales
  useEffect(() => {
    if (
      response &&
      response.list &&
      response.list.length > 0 &&
      !loading &&
      !limitSize
    ) {
      recalculateColumnWidths();
    }
  }, [
    response,
    loading,
    limitSize,
    headers,
    currentPage,
    recalculateColumnWidths
  ]);

  // Efecto para verificar scroll horizontal cuando cambian los datos o el tamaño de la ventana
  useEffect(() => {
    if (
      !response ||
      !response.list ||
      response.list.length === 0 ||
      loading ||
      limitSize
    ) {
      setNeedsHorizontalScroll(false);
      return;
    }

    const checkScroll = () => {
      // Pequeño delay para asegurar que el DOM se haya actualizado
      setTimeout(() => {
        checkHorizontalScrollNeeded();
      }, 100);
    };

    // Verificar después de que se complete el renderizado
    checkScroll();

    // También verificar cuando cambia el tamaño de la ventana
    window.addEventListener("resize", checkScroll);

    return () => {
      window.removeEventListener("resize", checkScroll);
    };
  }, [response, loading, limitSize, currentPage, checkHorizontalScrollNeeded]);

  // Efecto secundario para recalcular anchos después de re-renders internos
  // Este efecto se ejecuta después de cada render para detectar cambios en el DOM interno
  // (como cuando se actualiza el estado de checkboxes que causa re-render sin cambiar dependencias principales)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Defecto D8: este efecto no tiene array de dependencias, así que corre
    // después de CADA render (tildar un checkbox, abrir un select...). Combinado
    // con D3 hacía derivar los anchos con el uso. En el contrato los anchos
    // dependen sólo del CSS y de response/headers/currentPage/resize, que ya
    // cubre el efecto principal: acá no hace falta recalcular nada.
    if (widthContract) {
      return;
    }
    if (
      response &&
      response.list &&
      response.list.length > 0 &&
      !loading &&
      !limitSize
    ) {
      const tbody = tableTbodyRef.current?.querySelector("tbody");
      if (!tbody) {
        return;
      }

      // Limpiar timeout anterior si existe
      if (recalculationTimeoutRef.current !== undefined) {
        clearTimeout(recalculationTimeoutRef.current);
      }

      // Programar recálculo con un pequeño delay para permitir que React complete el renderizado
      // Esto capturará cambios internos como actualizaciones de checkboxes
      recalculationTimeoutRef.current = window.setTimeout(() => {
        // Solo recalcular si los elementos están presentes
        const hasElements =
          tableTbodyRef.current?.querySelector("tbody")?.children.length > 0;
        if (hasElements) {
          recalculateColumnWidths();
          // También verificar scroll horizontal después del recálculo
          setTimeout(() => {
            checkHorizontalScrollNeeded();
          }, 10);
        }
        recalculationTimeoutRef.current = undefined;
      }, 50);

      return () => {
        if (recalculationTimeoutRef.current !== undefined) {
          clearTimeout(recalculationTimeoutRef.current);
          recalculationTimeoutRef.current = undefined;
        }
      };
    }
  });

  // Calcula el alto disponible entre el borde superior del cuerpo de la tabla
  // y el final del viewport, descontando el footer de totales y el paginador.
  const recalculateFillHeight = React.useCallback(() => {
    if (!fillHeight) {
      return;
    }
    const container = tableTbodyRef.current;
    if (!container) {
      return;
    }
    // Nunca más chico que el default previo (mh-55p = 55vh), aunque el footer
    // de totales sea muy alto (p. ej. muchas monedas distintas).
    const MIN_HEIGHT = Math.max(200, window.innerHeight * 0.55);
    const previousHeight = container.style.height;
    container.style.height = "";
    const top = container.getBoundingClientRect().top;
    const tfootHeight =
      tableTfootRef.current?.getBoundingClientRect().height ?? 0;
    const paginatorHeight =
      paginatorRef.current?.getBoundingClientRect().height ?? 0;
    const outerContainer = container.closest(".container-fluid.padding");
    const outerPaddingBottom = outerContainer
      ? parseFloat(getComputedStyle(outerContainer).paddingBottom || "0")
      : 0;
    const available =
      window.innerHeight -
      top -
      tfootHeight -
      paginatorHeight -
      outerPaddingBottom;
    const newHeight = Math.max(MIN_HEIGHT, available);
    const previousHeightValue = parseFloat(previousHeight);
    // Evitar escribir un alto que no cambió (o casi no cambió): reescribir
    // style.height en cada notificación del ResizeObserver que observa el
    // padre de este contenedor puede volver a disparar ese mismo observer
    // (si el padre ajusta su tamaño al del hijo), generando un loop de
    // resize que el browser corta con "ResizeObserver loop completed with
    // undelivered notifications".
    if (
      !isNaN(previousHeightValue) &&
      Math.abs(previousHeightValue - newHeight) < 1
    ) {
      container.style.height = previousHeight;
      return;
    }
    container.style.height = newHeight + "px";
  }, [fillHeight]);

  useLayoutEffect(() => {
    const container = tableTbodyRef.current;
    if (!container) {
      return;
    }
    recalculateFillHeight();
    const parent = container.parentElement;
    const observer = new ResizeObserver(() => {
      // Diferir a rAF: escribir estilos de forma sincrónica dentro del
      // callback del propio ResizeObserver que originó la notificación
      // puede volver a redimensionar el target observado dentro del mismo
      // ciclo de resolución del browser, lo que dispara "ResizeObserver
      // loop completed with undelivered notifications". Postergarlo a la
      // siguiente animation frame saca la escritura fuera de ese ciclo.
      if (fillHeightRafRef.current !== undefined) {
        cancelAnimationFrame(fillHeightRafRef.current);
      }
      fillHeightRafRef.current = requestAnimationFrame(() => {
        fillHeightRafRef.current = undefined;
        recalculateFillHeight();
      });
      if (columnWidthResizeTimeoutRef.current !== undefined) {
        window.clearTimeout(columnWidthResizeTimeoutRef.current);
      }
      columnWidthResizeTimeoutRef.current = window.setTimeout(() => {
        recalculateColumnWidths();
        columnWidthResizeTimeoutRef.current = undefined;
      }, 250);
    });
    if (parent) {
      observer.observe(parent);
    }
    window.addEventListener("resize", recalculateFillHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recalculateFillHeight);
      if (columnWidthResizeTimeoutRef.current !== undefined) {
        window.clearTimeout(columnWidthResizeTimeoutRef.current);
        columnWidthResizeTimeoutRef.current = undefined;
      }
      if (fillHeightRafRef.current !== undefined) {
        cancelAnimationFrame(fillHeightRafRef.current);
        fillHeightRafRef.current = undefined;
      }
    };
  }, [
    fillHeight,
    recalculateFillHeight,
    recalculateColumnWidths,
    response,
    loading,
    limitSize,
    currentPage
  ]);

  const component = Switch(
    [
      error && !loading,
      () => (
        <ErrorMessage
          onRefresh={reload}
          message={TranslationService.translate.ErrorLoadingList}
        />
      )
    ],
    [
      !loading && response && response.list && response.list.length === 0,
      <Empty message={emptyMessage} />
    ]
  ) ?? (
    <TableLoader loading={loading} response={response}>
      {title && (
        <div className="row d-flex align-items-center">
          {title && (
            <div className="col">
              <h5 className="h5">{title}</h5>
            </div>
          )}
        </div>
      )}

      {response && response.list && response.list.length > 0 && (
        <>
          {!limitSize && (
            <div
              className={
                "tableContainer overflow-y-hidden overflow-x-hidden" +
                (stickyHeader ? " sticky-top" : "") +
                widthContractClass
              }
              ref={tableTheadRef}
            >
              <table className="table genericTable table-ellipsis mb-0">
                <Headers headers={headers} limitSize={limitSize} />
              </table>
            </div>
          )}

          <div
            ref={tableTbodyRef}
            className={
              "tableContainer overflow-y-auto overflow-x-auto " +
              (additionalClass || (fillHeight ? "" : "mh-55p")) +
              (fillHeight ? " fill-height" : "") +
              (needsHorizontalScroll ? " needs-horizontal-scroll" : "") +
              widthContractClass
            }
            onScroll={handleScroll}
          >
            <table className="table genericTable table-ellipsis">
              {limitSize && <Headers headers={headers} limitSize={limitSize} />}
              <tbody>
                {response.list.map((item: any, index: number) => {
                  return <Item key={extractKey(item, index)} data={item} />;
                })}
              </tbody>
            </table>
          </div>

          {children !== undefined ? (
            <div
              ref={tableTfootRef}
              className={
                "tableContainer overflow-y-hidden overflow-x-hidden text-nowrap m-0" +
                widthContractClass
              }
            >
              <table className="table genericTable table-ellipsis mb-0">
                <tbody className="tableTotal">{children}</tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </TableLoader>
  );

  return (
    <>
      {component}
      <div ref={paginatorRef}>
        <Paginator isTable={true} pagesToShowDefault={4} />
      </div>
    </>
  );
};

/**
 * R7 — con el contrato el label que no entra en su columna se recorta con
 * ellipsis, así que necesita tooltip para seguir siendo legible. Sólo se muestra
 * si realmente está recortado. Mismo criterio que el `showTooltip` de las celdas
 * en InvoiceTableItem.
 */
const showHeaderTooltip = (elem: HTMLElement) => {
  const text = (elem.textContent || "").trim();
  const tip = Tooltip.getOrCreateInstance(elem, {
    trigger: "manual",
    container: "body",
    customClass: "tooltipIntiza"
  });

  if (elem.scrollWidth > elem.clientWidth && text) {
    elem.setAttribute("data-bs-original-title", text);
    (tip as unknown as { setContent?: (c: object) => void }).setContent?.({
      ".tooltip-inner": text
    });
    tip.show();
  } else {
    tip.hide();
    tip.dispose();
    elem.removeAttribute("data-bs-original-title");
  }
};

export const Headers = ({
  headers,
  limitSize
}: {
  headers: TableHeader[];
  limitSize: boolean;
}) => {
  // R5 / defecto D7: con el contrato el hover no toca el layout. El CSS ya
  // impide que la columna se expanda; acá se evita además que el ancho
  // expandido quede congelado inline hasta el próximo recálculo.
  const widthContract = headers.some((header) => header.widthContract);
  const { sort, setSortColumn } = useContext(TableContext);
  const sortClass =
    sort.sortDirection === SortDirection.Descending
      ? "fa-sort-down"
      : "fa-sort-up";
  const isAdditional = (item: TableHeader): item is TableHeaderWithFieldId =>
    item.key.includes("additional") && "fieldId" in item;
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const originalWidthsRef = useRef<Map<number, number>>(new Map());

  const handleHover: MouseEventHandler<HTMLTableCellElement> = (event) => {
    if (limitSize || widthContract) {
      return;
    }
    const isMouseEnter = event.type === "mouseenter";
    const setWidth = () => {
      let th = event.target as HTMLTableCellElement;
      while (th.tagName !== "TH") {
        th = th.parentElement as HTMLTableCellElement;
        if (th === null) {
          return;
        }
      }
      const tr = th.parentElement!;
      const trChildren = Array.from(tr.children);
      const index = trChildren.indexOf(th);

      // Verificar si este header corresponde a un campo adicional con Width definido
      const headerAtIndex = headers[index];
      const isAdditionalWithFixedWidth =
        headerAtIndex &&
        headerAtIndex.key.includes("additional") &&
        "fieldId" in headerAtIndex;

      if (isAdditionalWithFixedWidth) {
        const definition = CompanyService.getAdditionalDefinitions().find(
          (x) =>
            x.AdditionalDefinitionID.toString() ===
            (headerAtIndex as TableHeaderWithFieldId).fieldId
        );
        // Si tiene Width definido, no aplicar estilos inline (dejar que las clases CSS funcionen)
        if (definition?.Width) {
          return;
        }
      }

      if (isMouseEnter) {
        // Detectar si el texto está truncado antes de expandir
        const textElement = th.querySelector(
          "div.ellipsis-oneline"
        ) as HTMLElement;
        const isTextTruncated = textElement
          ? textElement.scrollWidth > textElement.clientWidth
          : false;

        // Si el texto NO está truncado, no expandir - prevenir el comportamiento del CSS
        if (!isTextTruncated) {
          // Prevenir que el CSS hover expanda la columna estableciendo max-width al ancho actual
          const currentWidth = th.offsetWidth;
          th.style.maxWidth = currentWidth + "px";
          return; // No hacer nada más, el CSS hover no expandirá porque max-width está fijado
        }

        // Guardar el ancho original desde minWidth o maxWidth
        // El sistema de cálculo aplica el mismo valor a width, minWidth y maxWidth
        // Cuando el CSS aplica width: auto en hover, minWidth y maxWidth mantienen el valor original
        let originalWidth: number;
        const minWidthStr = th.style.minWidth;
        const maxWidthStr = th.style.maxWidth;
        const widthStr = th.style.width;

        // Leer desde minWidth o maxWidth primero (mantienen el valor original incluso con width: auto)
        if (minWidthStr && !isNaN(parseFloat(minWidthStr))) {
          originalWidth = parseFloat(minWidthStr);
        } else if (maxWidthStr && !isNaN(parseFloat(maxWidthStr))) {
          originalWidth = parseFloat(maxWidthStr);
        } else if (
          widthStr &&
          widthStr !== "auto" &&
          !isNaN(parseFloat(widthStr))
        ) {
          // Si minWidth y maxWidth no están, intentar desde width
          originalWidth = parseFloat(widthStr);
        } else {
          // Fallback: usar offsetWidth (aunque podría ser el expandido si el CSS ya se aplicó)
          originalWidth = th.offsetWidth;
        }

        // Solo guardar si no lo tenemos ya (para evitar sobrescribir si el usuario hace hover múltiples veces)
        if (!originalWidthsRef.current.has(index)) {
          originalWidthsRef.current.set(index, originalWidth);
        }

        // En mouseenter: el CSS expande el header, luego fijamos el ancho expandido
        const thWidth = th.offsetWidth;
        // Aplicar también al th para mantener sincronización
        th.style.minWidth = thWidth + "px";
        th.style.maxWidth = thWidth + "px";
        th.style.width = thWidth + "px";

        let thParent = th;
        while (thParent?.classList.contains("table-loader-container")) {
          thParent = thParent?.parentElement as HTMLTableCellElement;
          if (!thParent) {
            return;
          }
        }
        const tdList = thParent.querySelectorAll(`td:nth-child(${index + 1})`);
        const tds = Array.from(tdList) as HTMLTableCellElement[];
        // Aplicar a todos los td correspondientes
        tds.forEach((td) => {
          td.style.minWidth = thWidth + "px";
          td.style.maxWidth = thWidth + "px";
          td.style.width = thWidth + "px";
        });
      } else {
        // En mouseleave: restaurar el ancho original calculado solo si lo habíamos guardado (texto truncado)
        const originalWidth = originalWidthsRef.current.get(index);
        if (originalWidth !== undefined) {
          th.style.minWidth = originalWidth + "px";
          th.style.maxWidth = originalWidth + "px";
          th.style.width = originalWidth + "px";

          let thParent = th;
          while (thParent?.classList.contains("table-loader-container")) {
            thParent = thParent?.parentElement as HTMLTableCellElement;
            if (!thParent) {
              return;
            }
          }
          const tdList = thParent.querySelectorAll(
            `td:nth-child(${index + 1})`
          );
          const tds = Array.from(tdList) as HTMLTableCellElement[];
          // Restaurar ancho original en todos los td correspondientes
          tds.forEach((td) => {
            td.style.minWidth = originalWidth + "px";
            td.style.maxWidth = originalWidth + "px";
            td.style.width = originalWidth + "px";
          });

          originalWidthsRef.current.delete(index);
        } else {
          // Si no hay ancho guardado, significa que el texto no estaba truncado
          // Limpiar el max-width que establecimos para prevenir la expansión
          th.style.maxWidth = "";
        }
      }
    };
    setTimeout(setWidth, 20);
  };

  return (
    <thead ref={theadRef}>
      <tr>
        {headers.map((item: TableHeader, index: number) => {
          const onClick = item.sorteable
            ? () => setSortColumn(item.key)
            : undefined;
          let className = "ellipsis-oneline";
          let widthClass = "";
          if (isAdditional(item)) {
            const definition = CompanyService.getAdditionalDefinitions().find(
              (x) => x.AdditionalDefinitionID.toString() === item.fieldId
            );
            if (definition?.Width === 160) {
              widthClass = " th-w-160";
            } else if (definition?.Width === 240) {
              widthClass = " th-w-240";
            } else if (!item.numeric && !item.widthContract) {
              className += " w-100px";
            }
          } else {
            if (item.key === "group") {
              const withField = CompanyService.getConfigItems().find(
                (x) => x.field === -7
              )?.Width;

              if (withField === 160) {
                widthClass = " th-w-160";
              } else if (withField === 240) {
                widthClass = " th-w-240";
              }
            }
          }

          const headerClassName =
            (item.className ?? "") +
            (item.numeric ? " header-numeric" : "") +
            widthClass;
          const sortLimitClass = limitSize ? " super-text" : "";

          return (
            <th
              onMouseEnter={handleHover}
              onMouseLeave={handleHover}
              key={index}
              onClick={onClick}
              className={headerClassName}
              role={item.sorteable ? "button" : ""}
            >
              {(item.tooltip !== "" && (
                <TooltipComponent title={item.tooltip}>
                  <div className={className}>{item.label()}</div>
                </TooltipComponent>
              )) || (
                <div
                  className={className}
                  onMouseEnter={
                    item.widthContract
                      ? (event) => showHeaderTooltip(event.currentTarget)
                      : undefined
                  }
                  onMouseLeave={
                    item.widthContract
                      ? (event) =>
                          Tooltip.getInstance(event.currentTarget)?.hide()
                      : undefined
                  }
                >
                  {item?.label && item.label()}
                </div>
              )}
              {item.sorteable && item.key === sort.sortColumn ? (
                <i className={"mx-1 fas " + sortClass + sortLimitClass} />
              ) : (
                <></>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default Table;
